/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router-dom";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";

import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Card, CardContent, CardHeader } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { Cancel } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getColorList } from "app/redux/slice/colorSlice";
import { getCollectionList } from "app/redux/slice/collectionSlice";
import { useSnackbar } from "notistack";
import { PRODUCTS } from "app/routes/constant";
import {
  createProduct,
  getByIdProduct,
  updateProduct,
} from "app/redux/slice/productSlice";
import ProductImages from "../product-images/index";
import { deleteVariant } from "app/redux/slice/variantSlice";

// Seeded into the KG box the first time Set KG is switched on. Admin can change
// it straight away - a starting point, not a floor or a cap.
const DEFAULT_KG = 150;

const AddProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const colorData = useSelector((state) => state?.color);
  const collectionData = useSelector((state) => state?.collection);
  const { id } = useParams();
  const [product, setProduct] = useState();
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    name: Yup.string().nullable().required("The name is required"),
    prefix: Yup.string().nullable().required("The sku is required"),
    price: id
      ? Yup.number()
          .nullable()
          .typeError("Price must be a number")
          .min(0, "Price cannot be negative")
      : Yup.number()
          .typeError("Price must be a number")
          .required("Please enter price")
          .moreThan(0, "Price must be greater than 0"),
    gsm: Yup.number()
      .nullable()
      // An empty box means "not recorded", so it must reach the API as null
      // rather than being coerced to 0 by the number cast.
      .transform((value, original) =>
        original === "" || original === null ? null : value,
      )
      .typeError("GSM must be a number")
      .min(0, "GSM cannot be negative"),
    // Only required while the Set KG switch is on - off, the App falls back to
    // its own 25 KG default and this box is irrelevant.
    kg: Yup.number()
      .nullable()
      .transform((value, original) =>
        original === "" || original === null ? null : value,
      )
      .typeError("KG must be a number")
      .when("isKgSet", {
        is: true,
        then: (schema) =>
          schema
            .required("Please enter the KG value")
            .moreThan(0, "KG must be greater than 0"),
        otherwise: (schema) => schema,
      }),
    description: Yup.string()
      .nullable()
      .max(2000, "Description cannot be longer than 2000 characters"),
    variants: Yup.array().of(
      Yup.object({
        color: Yup.object().nullable().required("The color is required"),
        sku: Yup.string().required("The sku is required"),
        quantity: Yup.number()
          .nullable()
          .typeError("Quantity must be a number")
          .required("Please enter quantity")
          .min(0, "Quantity cannot be negative")
          .integer("Quantity must be a whole number"),
        lowStock: Yup.number()
          .nullable()
          .typeError("Low stock must be a number")
          .min(0, "Low stock cannot be negative")
          .integer("Low stock must be a whole number"),
      }),
    ),
  });

  useEffect(() => {
    dispatch(getColorList());
    dispatch(getCollectionList({ status: true }));
  }, []);

  // Extracted so the photo section can refetch the product after an upload,
  // delete or reorder without duplicating the unwrapping.
  const loadProduct = useCallback(() => {
    if (!id) return;
    dispatch(getByIdProduct(id))
      .unwrap()
      .then((response) => {
        if (response.status === 200) {
          setProduct(response?.data?.data?.productDetail);
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  }, [dispatch, enqueueSnackbar, id]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleDeleteVariant = (variantId) => {
    dispatch(deleteVariant(variantId))
      .unwrap()
      .then((response) => {
        if (response.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      });
  };

  return (
    <>
      <Card>
        <CardHeader title={`${!id ? "Add" : "Update"} Product`} />
        <CardContent>
          <Formik
            initialValues={{
              name: product?.name ?? "",
              prefix: product?.prefix ?? "",
              quantity: product?.quantity ?? "",
              price: product?.price ?? 0,
              // Blank, not 0 - a product with no recorded weight should show an
              // empty box.
              gsm: product?.gsm ?? "",
              isKgSet: product?.isKgSet ?? false,
              // Blank until the switch is turned on, which seeds the default.
              kg: product?.kg ?? "",
              description: product?.description ?? "",
              collection: product?.collection ?? null,
              collectionId: product?.collection?._id ?? null,
              variants: product?.variants?.map((variant) => ({
                color: variant?.color ?? null,
                _id: variant?._id,
                colorId: variant?.color?._id ?? null,
                sku: variant?.sku ?? "",
                quantity: variant?.quantity ?? 0,
                lowStock: variant?.lowStock ?? 0,
                // price: variant?.price ?? 0,
              })) ?? [
                {
                  color: null,
                  colorId: null,
                  sku: "",
                  quantity: 0,
                  lowStock: 0,
                  // price: 0,
                },
              ],
            }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={false}
            onSubmit={async (data, { setSubmitting }) => {
              // Prepare variants array with only required fields
              const variants = data.variants.map((obj) => ({
                colorId: obj.colorId,
                sku: obj.sku,
                quantity: obj.quantity,
                lowStock: obj.lowStock,
                _id: obj._id, // keep _id if present for update
              }));
              data.quantity = variants.reduce(
                (sum, obj) => sum + Number(obj.quantity),
                0,
              );
              const submitData = {
                ...data,
                // With the switch off there is no override to store, so the
                // saved value is cleared rather than left behind to confuse the
                // next person who opens the form.
                kg: data.isKgSet ? Number(data.kg) : null,
                collection: undefined,
                // Photos are managed by their own endpoints. The update
                // service does a blind $set of the body, so letting `images`
                // ride along here would overwrite the saved photo order.
                images: undefined,
                variants,
              };

              setSubmitting(true);
              const response = await dispatch(
                id
                  ? updateProduct({ id, data: submitData })
                  : createProduct(submitData),
              ).unwrap();
              if (response.status === 200) {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "success",
                });
                navigate(PRODUCTS);
                setSubmitting(false);
              } else {
                enqueueSnackbar(response.data?.responseMessage, {
                  variant: "error",
                });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <div>
                  <div>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <JumboAppTextField
                          fullWidth
                          name={`name`}
                          label="Product Name*"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <JumboAppTextField
                          fullWidth
                          name={`prefix`}
                          label="Sku*"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          disabled
                          sx={{ width: "100%" }}
                          name="quantity"
                          type="text"
                          label="Qty*"
                          value={values?.variants?.reduce(
                            (sum, item) => sum + Number(item.quantity),
                            0,
                          )}
                          error={touched?.quantity && errors?.quantity}
                          helperText={touched?.quantity && errors?.quantity}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <JumboAutoComplete
                          options={collectionData?.list ?? []}
                          label="Collection"
                          size="large"
                          getOptionLabel={(option) => option?.name}
                          value={values?.collection}
                          name="collection"
                          onChange={(evt, value) => {
                            setFieldValue("collection", value);
                            setFieldValue("collectionId", value?._id ?? null);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          sx={{ width: "100%" }}
                          name="price"
                          type="number"
                          label={id ? "Price" : "Price*"}
                          value={values?.price}
                          inputProps={{ min: 0, step: "any" }}
                          // A number input changes value when the wheel is
                          // scrolled over it while focused - dropping focus
                          // stops a scroll past the form silently editing it.
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => {
                            setFieldValue("price", +e.target.value);
                          }}
                          error={touched?.price && Boolean(errors?.price)}
                          helperText={touched?.price && errors?.price}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          sx={{ width: "100%" }}
                          name="gsm"
                          type="number"
                          label="GSM"
                          value={values?.gsm ?? ""}
                          inputProps={{ min: 0, step: "any" }}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => {
                            const next = e.target.value;
                            setFieldValue("gsm", next === "" ? null : +next);
                          }}
                          error={touched?.gsm && Boolean(errors?.gsm)}
                          helperText={touched?.gsm && errors?.gsm}
                        />
                      </Grid>

                      {/* -- Set KG --
                          Off, the App uses its own built-in 25 KG per roll.
                          On, it uses the number entered here instead. */}
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="isKgSet"
                              checked={!!values?.isKgSet}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setFieldValue("isKgSet", on);
                                // Turning it on with an empty box seeds the
                                // default so Admin has something to adjust
                                // rather than an empty required field. An
                                // existing value is never overwritten.
                                if (on && (values?.kg === "" || values?.kg == null)) {
                                  setFieldValue("kg", DEFAULT_KG);
                                }
                              }}
                              color="success"
                            />
                          }
                          label="Set KG"
                        />
                      </Grid>
                      {values?.isKgSet && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            sx={{ width: "100%" }}
                            name="kg"
                            type="number"
                            label="KG*"
                            value={values?.kg ?? ""}
                            inputProps={{ min: 0, step: "any" }}
                            onWheel={(e) => e.target.blur()}
                            onChange={(e) => {
                              const next = e.target.value;
                              setFieldValue("kg", next === "" ? "" : +next);
                            }}
                            error={touched?.kg && Boolean(errors?.kg)}
                            helperText={
                              (touched?.kg && errors?.kg) ||
                              "Per-roll weight the app will use for this product"
                            }
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          sx={{ width: "100%" }}
                          name="description"
                          label="Description"
                          multiline
                          minRows={3}
                          value={values?.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched?.description && Boolean(errors?.description)
                          }
                          helperText={
                            (touched?.description && errors?.description) ||
                            "Shown on the product detail screen in the app"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={12} mb={2}>
                        <Typography variant={"h5"} mb={0}>
                          Variant
                        </Typography>
                      </Grid>
                    </Grid>
                    <FieldArray name={`variants`}>
                      {({ remove: removeVariant, push: pushVariant }) => (
                        <div>
                          {values?.variants?.length > 0 &&
                            values?.variants?.map(
                              (valVariant, indexVariant) => (
                                <div key={indexVariant}>
                                  <Grid container spacing={3} mb={3}>
                                    <Grid item xs={12} sm={3.5}>
                                      <JumboAutoComplete
                                        options={colorData.list ?? []}
                                        label="Color*"
                                        size="large"
                                        getOptionLabel={(option) =>
                                          option?.name
                                        }
                                        value={valVariant?.color}
                                        name={`variants[${indexVariant}].color`}
                                        onChange={(evt, value) => {
                                          setFieldValue(
                                            `variants[${indexVariant}].color`,
                                            value,
                                          );
                                          setFieldValue(
                                            `variants[${indexVariant}].colorId`,
                                            value?._id,
                                          );
                                          const sku = `${values?.prefix}-${
                                            value?.prefix || ""
                                          }`;
                                          setFieldValue(
                                            `variants[${indexVariant}].sku`,
                                            sku.toUpperCase(),
                                          );
                                        }}
                                        helperText={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant]
                                                .color
                                            : ""
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={3.5}>
                                      <TextField
                                        sx={{ width: "100%" }}
                                        name={`variants[${indexVariant}].sku`}
                                        type="text"
                                        label="SKU*"
                                        onChange={(e) => {
                                          setFieldValue(
                                            `variants[${indexVariant}].sku`,
                                            e.target.value,
                                          );
                                        }}
                                        value={valVariant?.sku}
                                        error={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant].sku
                                            : ""
                                        }
                                        helperText={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant].sku
                                            : ""
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={1.75}>
                                      <TextField
                                        sx={{ width: "100%" }}
                                        name={`variants[${indexVariant}].quantity`}
                                        type="text"
                                        label="Qty*"
                                        onChange={(e) => {
                                          setFieldValue(
                                            `variants[${indexVariant}].quantity`,
                                            +e.target.value,
                                          );
                                          setFieldValue(
                                            `quantity`,
                                            values?.variants?.reduce(
                                              (sum, item) =>
                                                sum + Number(item.quantity),
                                              0,
                                            ),
                                          );
                                        }}
                                        value={valVariant?.quantity}
                                        error={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant]
                                                .quantity
                                            : ""
                                        }
                                        helperText={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant]
                                                .quantity
                                            : ""
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={1.75}>
                                      <TextField
                                        sx={{ width: "100%" }}
                                        name={`variants[${indexVariant}].lowStock`}
                                        type="text"
                                        label="Low Stock"
                                        onChange={(e) => {
                                          setFieldValue(
                                            `variants[${indexVariant}].lowStock`,
                                            +e.target.value,
                                          );
                                        }}
                                        value={valVariant?.lowStock}
                                        error={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant]
                                                .lowStock
                                            : ""
                                        }
                                        helperText={
                                          errors &&
                                          errors.variants &&
                                          errors.variants[indexVariant]
                                            ? errors.variants[indexVariant]
                                                .lowStock
                                            : ""
                                        }
                                      />
                                    </Grid>
                                    {/* <Grid item xs={12} sm={2.5}>
                                    <TextField
                                      sx={{ width: "100%" }}
                                      name={`variants[${indexVariant}].price`}
                                      type="text"
                                      label="Price*"
                                      onChange={(e) => {
                                        setFieldValue(
                                          `variants[${indexVariant}].price`,
                                          +e.target.value
                                        );
                                      }}
                                      value={valVariant?.price}
                                      error={
                                        errors &&
                                        errors.variants &&
                                        errors.variants[indexVariant]
                                          ? errors.variants[indexVariant].price
                                          : ""
                                      }
                                      helperText={
                                        errors &&
                                        errors.variants &&
                                        errors.variants[indexVariant]
                                          ? errors.variants[indexVariant].price
                                          : ""
                                      }
                                    />
                                  </Grid> */}
                                    {indexVariant !== 0 && (
                                      <Grid item xs={12} sm={1}>
                                        <IconButton
                                          size="large"
                                          onClick={() => {
                                            removeVariant(indexVariant);
                                            if (valVariant?._id) {
                                              handleDeleteVariant(
                                                valVariant?._id,
                                              );
                                            }
                                          }}
                                        >
                                          <Cancel />
                                        </IconButton>
                                      </Grid>
                                    )}
                                    {indexVariant === 0 && (
                                      <Grid item xs={12} sm={1.5}>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          sx={{ mt: 1.5 }}
                                          onClick={() =>
                                            pushVariant({
                                              color: null,
                                              sku: "",
                                              quantity: null,
                                              lowStock: 0,
                                            })
                                          }
                                        >
                                          Add More
                                        </Button>
                                      </Grid>
                                    )}
                                  </Grid>
                                </div>
                              ),
                            )}
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  <br></br>
                </div>

                <Divider sx={{ mb: 3 }} />
                {id ? (
                  <ProductImages
                    productId={id}
                    images={product?.images || []}
                    onChanged={loadProduct}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Save the product first, then you can add photos to it.
                  </Typography>
                )}

                <Stack
                  spacing={{ xs: 1, sm: 2 }}
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ width: "100%", mt: 3 }}
                >
                  <LoadingButton
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      navigate(PRODUCTS);
                    }}
                    fullWidth={mobileView}
                  >
                    Cancel
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    loadingIndicator={<CircularProgress size={20} />}
                    fullWidth={mobileView}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};

export default AddProduct;
